import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { connect, useDispatch } from "react-redux";
import { LuEye, LuEyeOff, LuMailCheck } from "react-icons/lu";
import { signupAction, loadingToggleAction } from "../../store/actions/AuthActions";
import { Brand, Field, Note } from "../components/Ui";
import SEO from "../components/SEO";
import "./auth.css";

function Registration(props) {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  /* legacy initialized first/last name to a single space — the untouched
     payload sends " ", so the quirk is kept for byte-parity */
  const [firstName, setFirstName] = useState(" ");
  const [lastName, setLastName] = useState(" ");
  const [country, setCountry] = useState("");
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});

  const registered = props.errorMessage === "User registered successfully";

  useEffect(() => {
    if (registered) {
      const timer = setTimeout(() => navigate("/login"), 5000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [registered, navigate]);

  useEffect(() => {
    setErrors((prev) => ({
      ...prev,
      confirmPassword: confirmPassword && password !== confirmPassword ? "Passwords do not match" : "",
    }));
  }, [password, confirmPassword]);

  function onSignUp(e) {
    e.preventDefault();
    const next = {};
    if (!country) next.country = "Country is required";
    if (!email) next.email = "Email is required";
    if (!password) next.password = "Password is required";
    if (!confirmPassword) next.confirmPassword = "Confirm your password";
    if (password && confirmPassword && password !== confirmPassword) next.confirmPassword = "Passwords do not match";
    setErrors(next);
    if (Object.keys(next).some((k) => next[k])) return;

    dispatch(loadingToggleAction(true));
    /* argument order intentionally mirrors the legacy call: the API mapping depends on it */
    dispatch(signupAction(firstName, lastName, country, username, email, password, navigate));
  }

  /* legacy button was disabled until both passwords were typed and matched */
  const passwordsMatch = password && confirmPassword && password === confirmPassword;

  /* same friendly copy the legacy popups used for these backend messages */
  let showError = "";
  if (props.errorMessage && !registered && props.errorMessage !== "") {
    const m = String(props.errorMessage);
    if (m === "An error occurred") {
      showError = "Oops! It looks like this email is already registered.";
    } else if (m.includes("incorrect") || m.includes("invalid") || m === "Invalid credentials") {
      showError =
        "The password you entered is incorrect. Please double-check and enter the correct password to continue.";
    } else {
      showError = m;
    }
  }

  return (
    <div className="csx">
      <SEO
        title="Create your free account"
        description="Create a free CareerSavvy account for AI job matching, resume tailoring and interview practice."
        canonicalPath="/page-register"
        breadcrumbs={[{ label: "Create account", path: "/page-register" }]}
      />
      <div className="csx-page">
        <main className="auth-hero">
          <div className="auth-mesh" aria-hidden="true" />
          <div className="auth-box">
            <div className="auth-brand">
              <Brand />
            </div>

            <div className="ld-glass auth-panel stack-3">
              <div className="stack-2">
                <h1 className="auth-title">Create your account</h1>
                <p className="t-sm">Takes a minute. No credit card required.</p>
              </div>

              {registered ? (
                <Note tone="accent">
                  <LuMailCheck size={17} />
                  <span>
                    Account created. Check your email for the activation link — redirecting you to sign in…
                  </span>
                </Note>
              ) : null}
              {showError ? (
                <Note tone="danger">
                  <span>{showError}</span>
                </Note>
              ) : null}

              <form className="stack-3" onSubmit={onSignUp} noValidate>
                <div className="grid-2" style={{ gap: 12 }}>
                  <Field label="First name">
                    <input className="input" value={firstName} placeholder="First name" onChange={(e) => setFirstName(e.target.value)} />
                  </Field>
                  <Field label="Last name">
                    <input className="input" value={lastName} placeholder="Last name" onChange={(e) => setLastName(e.target.value)} />
                  </Field>
                </div>
                <Field label="Username">
                  <input className="input" value={username} placeholder="Pick a username" onChange={(e) => setUsername(e.target.value)} />
                </Field>
                <div className="grid-2" style={{ gap: 12 }}>
                  <Field label="Country" error={errors.country}>
                    {/* same option set as the legacy select — the backend
                        expects one of these values */}
                    <select
                      className="input"
                      value={country}
                      aria-invalid={!!errors.country}
                      onChange={(e) => setCountry(e.target.value)}
                    >
                      <option value="">Select Country</option>
                      <option value="USA">USA</option>
                      <option value="India">India</option>
                    </select>
                  </Field>
                  <Field label="Email" error={errors.email}>
                    <input
                      className="input"
                      type="email"
                      autoComplete="email"
                      value={email}
                      placeholder="you@example.com"
                      aria-invalid={!!errors.email}
                      onChange={(e) => setEmail(e.target.value)}
                    />
                  </Field>
                </div>
                <Field label="Password" error={errors.password}>
                  <div className="input-wrap">
                    <input
                      className="input"
                      type={showPassword ? "text" : "password"}
                      autoComplete="new-password"
                      value={password}
                      placeholder="Create a password"
                      aria-invalid={!!errors.password}
                      onChange={(e) => setPassword(e.target.value)}
                      style={{ paddingRight: 44 }}
                    />
                    <span className="input-trail">
                      <button type="button" aria-label="Toggle password visibility" onClick={() => setShowPassword((v) => !v)}>
                        {showPassword ? <LuEyeOff size={17} /> : <LuEye size={17} />}
                      </button>
                    </span>
                  </div>
                </Field>
                <Field label="Confirm password" error={errors.confirmPassword}>
                  <input
                    className="input"
                    type={showPassword ? "text" : "password"}
                    autoComplete="new-password"
                    value={confirmPassword}
                    placeholder="Repeat the password"
                    aria-invalid={!!errors.confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                  />
                </Field>
                <button
                  type="submit"
                  className="btn btn-mint btn-lg btn-full"
                  disabled={!passwordsMatch || props.showLoading || registered}
                >
                  {props.showLoading ? <span className="spinner" /> : null}
                  Create account
                </button>
              </form>

              <p className="t-xs t-center">
                By creating an account you agree to the CareerSavvy{" "}
                <Link to="/terms" className="t-accent" style={{ fontWeight: 600 }}>
                  Terms of Service
                </Link>{" "}
                and{" "}
                <Link to="/privacy" className="t-accent" style={{ fontWeight: 600 }}>
                  Privacy Policy
                </Link>
                .
              </p>

              <p className="t-sm t-center">
                Already have an account?{" "}
                <Link to="/login" className="t-accent" style={{ fontWeight: 600 }}>
                  Sign in
                </Link>
              </p>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}

const mapStateToProps = (state) => ({
  errorMessage: state.auth.errorMessage,
  successMessage: state.auth.successMessage,
  showLoading: state.auth.showLoading,
});

export default connect(mapStateToProps)(Registration);
